// captions/useCaptions.ts — a video's caption tracks and cues, fetched once and shared by the
// player's captions and the watch page's transcript (1.27.60). A failed fetch just means no
// captions; nothing about watching depends on it.
import { useEffect, useState } from 'react';
import type { CaptionTrack } from '@/core/models/post';
import { captionsService } from '@/core/services/captionsService';
import { parseVtt, type Cue } from './vtt';

const tracksCache = new Map<string, Promise<CaptionTrack[]>>();
const cuesCache = new Map<string, Promise<Cue[]>>();

function tracksFor(videoId: string): Promise<CaptionTrack[]> {
  let p = tracksCache.get(videoId);
  if (!p) {
    p = captionsService.list(videoId).catch(() => []);
    tracksCache.set(videoId, p);
  }
  return p;
}

function cuesFor(videoId: string, language: string): Promise<Cue[]> {
  const key = `${videoId}:${language}`;
  let p = cuesCache.get(key);
  if (!p) {
    p = captionsService
      .file(videoId, language)
      .then(parseVtt)
      .catch(() => []);
    cuesCache.set(key, p);
  }
  return p;
}

/** Forget what we fetched — after the uploader adds or removes a track. */
export function invalidateCaptions(videoId: string): void {
  tracksCache.delete(videoId);
  for (const key of [...cuesCache.keys()]) if (key.startsWith(`${videoId}:`)) cuesCache.delete(key);
}

/** The track to show: the viewer's chosen language when this video has it, else the first. */
export function pickTrack(tracks: CaptionTrack[] | null, preferred: string): CaptionTrack | null {
  if (!tracks?.length) return null;
  return tracks.find((t) => t.language === preferred) ?? tracks[0]!;
}

/** null while loading; [] when the video has none. */
export function useCaptionTracks(videoId: string | null, version = 0): CaptionTrack[] | null {
  const [state, setState] = useState<{ key: string; tracks: CaptionTrack[] } | null>(null);
  const key = `${videoId}:${version}`;
  useEffect(() => {
    if (!videoId) return;
    let live = true;
    void tracksFor(videoId).then((tracks) => live && setState({ key, tracks }));
    return () => {
      live = false;
    };
  }, [videoId, key]);
  if (!videoId) return [];
  return state?.key === key ? state.tracks : null;
}

/** The cues of one track, or null while loading / when `language` is null. */
export function useCues(videoId: string | null, language: string | null): Cue[] | null {
  const [state, setState] = useState<{ key: string; cues: Cue[] } | null>(null);
  const key = `${videoId}:${language}`;
  useEffect(() => {
    if (!videoId || !language) return;
    let live = true;
    void cuesFor(videoId, language).then((cues) => live && setState({ key, cues }));
    return () => {
      live = false;
    };
  }, [videoId, language, key]);
  return videoId && language && state?.key === key ? state.cues : null;
}
